import { z } from 'zod';
import {
  ACCESS_INSTRUCTION_KINDS,
  COMPENSATION_METHODS,
  CONTRACT_TERMS,
  CONVERSATION_PRIORITIES,
  CONVERSATION_STATUSES,
  EMPLOYEE_STATUSES,
  JOB_PRIORITIES,
  JOB_STATUSES,
  PAYROLL_STATUSES,
  PROPERTY_TYPES,
  QUOTE_STATUSES,
  SERVICE_FREQUENCIES,
} from '../enums';
import { PERMISSIONS } from '../permissions';

/**
 * Input schemas shared by the API (request validation) and the web app
 * (React Hook Form resolvers). Responses are typed in apps/web from the API.
 */

const localDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'invalid_date');
const localTime = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'invalid_time');
const optionalText = (max: number) => z.string().trim().max(max).optional().nullable();
const cents = z.number().int().min(0).max(100_000_000);
const csv = <T extends z.ZodTypeAny>(item: T) =>
  z.preprocess((v) => (typeof v === 'string' ? v.split(',').filter(Boolean) : v), z.array(item));

/** Query-string boolean: only "true"/"1" are true (qbool would read "false" as true). */
const qbool = z.preprocess((v) => (typeof v === 'string' ? v === 'true' || v === '1' : v), z.boolean());

export const zLocalDate = localDate;
export const zLocalTime = localTime;

export const paginationQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
});
export type PaginationQuery = z.infer<typeof paginationQuery>;

export const idParam = z.object({ id: z.uuid() });

// ---------------------------------------------------------------- auth
export const loginInput = z.object({
  email: z.email().max(254),
  password: z.string().min(1).max(200),
});
export const devLoginInput = z.object({ email: z.email().max(254) });
export const registerClientInput = z.object({
  fullName: z.string().trim().min(2).max(160),
  email: z.email().max(254),
  phone: optionalText(40),
  password: z.string().min(8).max(200),
  language: z.enum(['fr', 'en']).default('fr'),
});
export type LoginInput = z.infer<typeof loginInput>;
export type RegisterClientInput = z.infer<typeof registerClientInput>;

// ---------------------------------------------------------------- clients
export const clientInput = z.object({
  displayName: z.string().trim().min(2).max(160),
  email: z.email().max(254).optional().nullable().or(z.literal('')),
  phone: optionalText(40),
  secondaryPhone: optionalText(40),
  preferredLanguage: z.enum(['fr', 'en']).default('fr'),
  internalNotes: optionalText(4000),
  source: optionalText(40),
});
export type ClientInput = z.infer<typeof clientInput>;

export const clientListQuery = paginationQuery.extend({
  q: z.string().trim().max(100).optional(),
  sectorId: z.uuid().optional(),
  archived: qbool.optional(),
});

/** What a client can edit on their own profile. */
export const clientSelfUpdateInput = z.object({
  displayName: z.string().trim().min(2).max(160).optional(),
  phone: optionalText(40),
  secondaryPhone: optionalText(40),
  preferredLanguage: z.enum(['fr', 'en']).optional(),
});

// ---------------------------------------------------------------- properties
export const propertyInput = z.object({
  name: z.string().trim().min(1).max(120),
  addressLine1: z.string().trim().min(3).max(200),
  unit: optionalText(40),
  city: z.string().trim().min(1).max(100),
  province: z.string().trim().min(2).max(40).default('QC'),
  postalCode: optionalText(12),
  lat: z.number().min(-90).max(90).optional().nullable(),
  lng: z.number().min(-180).max(180).optional().nullable(),
  sectorId: z.uuid().optional().nullable(),
  propertyType: z.enum(PROPERTY_TYPES).default('HOUSE'),
  floors: z.number().int().min(1).max(10).default(1),
  bedrooms: z.number().int().min(0).max(20).default(1),
  bathrooms: z.number().int().min(0).max(20).default(1),
  kitchens: z.number().int().min(0).max(10).default(1),
  familyRooms: z.number().int().min(0).max(10).default(0),
  squareFeet: z.number().int().min(100).max(100_000).optional().nullable(),
  parkingInfo: optionalText(500),
  hasPets: z.boolean().default(false),
  petDetails: optionalText(200),
  permanentNotes: optionalText(4000),
  preferences: optionalText(2000),
  specialProducts: optionalText(1000),
  forbiddenAreas: optionalText(1000),
});
export type PropertyInput = z.infer<typeof propertyInput>;

export const accessInstructionInput = z.object({
  kind: z.enum(ACCESS_INSTRUCTION_KINDS),
  label: z.string().trim().min(1).max(120),
  secretValue: optionalText(500),
  notes: optionalText(1000),
});
export type AccessInstructionInput = z.infer<typeof accessInstructionInput>;

export const propertyNoteInput = z.object({
  visibility: z.enum(['INTERNAL', 'EMPLOYEE', 'CLIENT']).default('INTERNAL'),
  body: z.string().trim().min(1).max(4000),
});

export const propertyTaskAdjustmentInput = z.discriminatedUnion('action', [
  z.object({ action: z.literal('ADD'), section: z.string().min(1).max(32), label: z.string().trim().min(1).max(200) }),
  z.object({ action: z.literal('REMOVE'), taskTemplateItemId: z.uuid() }),
]);

// ---------------------------------------------------------------- services
export const taskTemplateItemInput = z.object({
  section: z.string().min(1).max(32),
  labelFr: z.string().trim().min(1).max(200),
  labelEn: z.string().trim().min(1).max(200),
  isRequired: z.boolean().default(true),
});
export const taskTemplateInput = z.object({
  code: z.string().trim().min(2).max(40).regex(/^[A-Z0-9_]+$/),
  nameFr: z.string().trim().min(1).max(120),
  nameEn: z.string().trim().min(1).max(120),
  description: optionalText(1000),
  items: z.array(taskTemplateItemInput).max(200),
});
export type TaskTemplateInput = z.infer<typeof taskTemplateInput>;

export const serviceTypeInput = z.object({
  nameFr: z.string().trim().min(1).max(120),
  nameEn: z.string().trim().min(1).max(120),
  descriptionFr: optionalText(1000),
  descriptionEn: optionalText(1000),
  priceMultiplier: z.number().min(0.1).max(10),
  durationMultiplier: z.number().min(0.1).max(10),
  defaultTaskTemplateId: z.uuid().optional().nullable(),
  isActive: z.boolean().default(true),
});

// ---------------------------------------------------------------- quotes
export const propertyDetailsInput = z.object({
  propertyType: z.enum(PROPERTY_TYPES),
  floors: z.number().int().min(1).max(10),
  bedrooms: z.number().int().min(0).max(20),
  bathrooms: z.number().int().min(0).max(20),
  kitchens: z.number().int().min(0).max(10),
  familyRooms: z.number().int().min(0).max(10),
  squareFeet: z.number().int().min(100).max(100_000).optional().nullable(),
  hasPets: z.boolean(),
  petDetails: optionalText(200),
});

/** Booking wizard (client) or manual request (staff, with clientId). */
export const quoteRequestInput = z.object({
  clientId: z.uuid().optional(),
  propertyId: z.uuid(),
  serviceTypeId: z.uuid(),
  /** Step 3: optional corrections of the property characteristics (saved on the property). */
  propertyDetails: propertyDetailsInput.optional(),
  requestedDate: localDate,
  requestedTime: localTime,
  frequency: z.enum(SERVICE_FREQUENCIES).default('ONE_TIME'),
  customIntervalDays: z.number().int().min(1).max(120).optional().nullable(),
  contractTerm: z.enum(CONTRACT_TERMS).default('NONE'),
  extras: z.array(z.string().max(40)).max(20).default([]),
  specialRequests: optionalText(2000),
});
export type QuoteRequestInput = z.infer<typeof quoteRequestInput>;

export const quoteLineInput = z.object({
  code: z.string().trim().min(1).max(40),
  label: z.string().trim().min(1).max(200),
  quantity: z.number().int().min(1).max(10_000),
  unitCents: z.number().int().min(-10_000_000).max(10_000_000),
});

export const quoteProposeInput = z.object({
  lines: z.array(quoteLineInput).min(1).max(50),
  estimatedMinutes: z.number().int().min(15).max(24 * 60),
  requiredWorkers: z.number().int().min(1).max(10),
  messageToClient: optionalText(2000),
  internalNote: optionalText(2000),
  validUntil: localDate.optional().nullable(),
  /** Send to the client right away. */
  send: z.boolean().default(false),
});
export type QuoteProposeInput = z.infer<typeof quoteProposeInput>;

export const quoteRespondInput = z.object({
  decision: z.enum(['ACCEPT', 'REJECT']),
  note: optionalText(2000),
});

export const quoteListQuery = paginationQuery.extend({
  status: csv(z.enum(QUOTE_STATUSES)).optional(),
  clientId: z.uuid().optional(),
  q: z.string().trim().max(100).optional(),
});

export const estimateInput = z.object({
  serviceTypeId: z.uuid(),
  propertyDetails: propertyDetailsInput,
  frequency: z.enum(SERVICE_FREQUENCIES).default('ONE_TIME'),
  extras: z.array(z.string().max(40)).max(20).default([]),
});

// ---------------------------------------------------------------- jobs
export const jobListQuery = paginationQuery.extend({
  from: localDate.optional(),
  to: localDate.optional(),
  status: csv(z.enum(JOB_STATUSES)).optional(),
  sectorId: z.uuid().optional(),
  employeeId: z.uuid().optional(),
  teamId: z.uuid().optional(),
  clientId: z.uuid().optional(),
  propertyId: z.uuid().optional(),
  q: z.string().trim().max(100).optional(),
});
export type JobListQuery = z.infer<typeof jobListQuery>;

export const jobCreateInput = z.object({
  clientId: z.uuid(),
  propertyId: z.uuid(),
  serviceTypeId: z.uuid(),
  date: localDate,
  time: localTime,
  estimatedMinutes: z.number().int().min(15).max(24 * 60).optional(),
  /** Omitted: computed by the pricing engine. Setting it requires quotes.approve. */
  priceClientCents: cents.optional(),
  requiredWorkers: z.number().int().min(1).max(10).default(1),
  priority: z.enum(JOB_PRIORITIES).default('NORMAL'),
  notes: optionalText(4000),
  specialInstructions: optionalText(4000),
});
export type JobCreateInput = z.infer<typeof jobCreateInput>;

export const jobUpdateInput = z.object({
  date: localDate.optional(),
  time: localTime.optional(),
  estimatedMinutes: z.number().int().min(15).max(24 * 60).optional(),
  priceClientCents: cents.optional(),
  requiredWorkers: z.number().int().min(1).max(10).optional(),
  priority: z.enum(JOB_PRIORITIES).optional(),
  notes: optionalText(4000),
  specialInstructions: optionalText(4000),
  /** Required when staff bypasses the client cutoff or another rule. */
  overrideReason: optionalText(500),
});
export type JobUpdateInput = z.infer<typeof jobUpdateInput>;

export const clientJobChangeInput = z.object({
  date: localDate.optional(),
  time: localTime.optional(),
  specialInstructions: optionalText(2000),
});

export const jobStatusInput = z.object({
  status: z.enum(JOB_STATUSES),
  reason: optionalText(500),
  completionNotes: optionalText(2000),
});

export const jobCancelInput = z.object({
  reason: z.string().trim().min(2).max(500),
  overrideReason: optionalText(500),
});

export const jobTaskToggleInput = z.object({ isDone: z.boolean() });
export const jobTaskAddInput = z.object({ section: z.string().min(1).max(32), label: z.string().trim().min(1).max(200) });

export const assignInput = z.object({
  employeeIds: z.array(z.uuid()).min(1).max(10),
  teamId: z.uuid().optional().nullable(),
  leadEmployeeId: z.uuid().optional().nullable(),
  /** Assign even though the engine reports hard conflicts (audited). */
  force: z.boolean().default(false),
  overrideReason: optionalText(500),
});
export type AssignInput = z.infer<typeof assignInput>;

export const routeQuery = z.object({
  date: localDate,
  employeeId: z.uuid().optional(),
  teamId: z.uuid().optional(),
});

// ---------------------------------------------------------------- agreements
export const agreementActionInput = z.discriminatedUnion('action', [
  z.object({ action: z.literal('PAUSE'), from: localDate, until: localDate.optional().nullable() }),
  z.object({ action: z.literal('RESUME') }),
  z.object({ action: z.literal('CANCEL'), reason: z.string().trim().min(2).max(500) }),
  z.object({
    action: z.literal('CHANGE_SCHEDULE'),
    frequency: z.enum(SERVICE_FREQUENCIES),
    intervalDays: z.number().int().min(1).max(120).optional().nullable(),
    startDate: localDate,
    startTime: localTime,
  }),
  z.object({ action: z.literal('CHANGE_PRICE'), pricePerVisitCents: cents, applyToFutureJobs: z.boolean().default(true) }),
]);
export type AgreementActionInput = z.infer<typeof agreementActionInput>;

// ---------------------------------------------------------------- employees & teams
const weeklyWindow = z.object({ weekday: z.number().int().min(0).max(6), startTime: localTime, endTime: localTime });

export const compensationInput = z.object({
  method: z.enum(COMPENSATION_METHODS),
  fixedCents: cents.optional().nullable(),
  hourlyRateCents: cents.optional().nullable(),
  percentage: z.number().min(0).max(1).optional().nullable(),
});
export type CompensationInput = z.infer<typeof compensationInput>;

export const employeeInput = z.object({
  fullName: z.string().trim().min(2).max(160),
  email: z.email().max(254),
  phone: optionalText(40),
  status: z.enum(EMPLOYEE_STATUSES).default('ACTIVE'),
  hireDate: localDate.optional().nullable(),
  hasVehicle: z.boolean().default(true),
  vehicleNotes: optionalText(200),
  skills: z.array(z.string().max(40)).max(20).default([]),
  sectorIds: z.array(z.uuid()).max(50).default([]),
  dailyCapacityJobs: z.number().int().min(1).max(20).optional().nullable(),
  maxHoursPerDay: z.number().min(1).max(16).optional().nullable(),
  homeAddress: optionalText(240),
  homeLat: z.number().min(-90).max(90).optional().nullable(),
  homeLng: z.number().min(-180).max(180).optional().nullable(),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/).default('#6b8f71'),
  internalNotes: optionalText(4000),
  availability: z.array(weeklyWindow).max(21).default([]),
  compensation: compensationInput.optional(),
});
export type EmployeeInput = z.infer<typeof employeeInput>;

export const timeOffInput = z.object({ startDate: localDate, endDate: localDate, reason: optionalText(200) });

export const teamInput = z.object({
  name: z.string().trim().min(1).max(120),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/).default('#6b8f71'),
  leaderEmployeeId: z.uuid().optional().nullable(),
  memberIds: z.array(z.uuid()).max(20).default([]),
  sectorIds: z.array(z.uuid()).max(50).default([]),
  dailyCapacityJobs: z.number().int().min(1).max(30).optional().nullable(),
  isActive: z.boolean().default(true),
});
export type TeamInput = z.infer<typeof teamInput>;

export const userPermissionsInput = z.object({
  granted: z.array(z.enum(PERMISSIONS)).default([]),
  revoked: z.array(z.enum(PERMISSIONS)).default([]),
});

export const staffUserInput = z.object({
  fullName: z.string().trim().min(2).max(160),
  email: z.email().max(254),
  phone: optionalText(40),
  role: z.enum(['ADMIN', 'SUPERVISOR']),
});

// ---------------------------------------------------------------- sectors
export const serviceAreaInput = z.object({ name: z.string().trim().min(1).max(120), description: optionalText(500) });
export const sectorInput = z.object({
  serviceAreaId: z.uuid(),
  name: z.string().trim().min(1).max(120),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/).default('#6b8f71'),
  centerLat: z.number().min(-90).max(90).optional().nullable(),
  centerLng: z.number().min(-180).max(180).optional().nullable(),
  radiusKm: z.number().min(0.5).max(200).optional().nullable(),
  isActive: z.boolean().default(true),
});

// ---------------------------------------------------------------- payroll
export const payrollListQuery = paginationQuery.extend({
  from: localDate.optional(),
  to: localDate.optional(),
  employeeId: z.uuid().optional(),
  status: csv(z.enum(PAYROLL_STATUSES)).optional(),
});
export const payrollActionInput = z.object({
  ids: z.array(z.uuid()).min(1).max(500),
  action: z.enum(['APPROVE', 'MARK_PAID', 'VOID']),
  reason: optionalText(500),
});

// ---------------------------------------------------------------- chat
export const conversationCreateInput = z.object({
  subject: optionalText(200),
  body: z.string().trim().min(1).max(5000),
  /** Staff only: open a conversation with a client or an employee. */
  clientId: z.uuid().optional(),
  employeeId: z.uuid().optional(),
  relatedJobId: z.uuid().optional(),
  relatedQuoteId: z.uuid().optional(),
  relatedPropertyId: z.uuid().optional(),
});
export const messageInput = z.object({
  body: z.string().trim().min(1).max(5000),
  internal: z.boolean().default(false),
});
export const conversationUpdateInput = z.object({
  status: z.enum(CONVERSATION_STATUSES).optional(),
  priority: z.enum(CONVERSATION_PRIORITIES).optional(),
  assignedToUserId: z.uuid().optional().nullable(),
  assignedTeamId: z.uuid().optional().nullable(),
});
export const conversationListQuery = paginationQuery.extend({
  status: csv(z.enum(CONVERSATION_STATUSES)).optional(),
  assignedToMe: qbool.optional(),
  unassigned: qbool.optional(),
  kind: z.enum(['CLIENT_SUPPORT', 'EMPLOYEE_SUPPORT', 'INTERNAL']).optional(),
});

// ---------------------------------------------------------------- payments
export const checkoutInput = z.object({ paymentId: z.uuid().optional(), jobId: z.uuid().optional(), agreementId: z.uuid().optional() });
export const paymentListQuery = paginationQuery.extend({
  clientId: z.uuid().optional(),
  status: csv(z.enum(['PENDING', 'AUTHORIZED', 'PAID', 'FAILED', 'REFUNDED', 'PARTIALLY_REFUNDED', 'CANCELLED'])).optional(),
});

// ---------------------------------------------------------------- search & analytics
export const searchQuery = z.object({ q: z.string().trim().min(2).max(100) });
export const rangeQuery = z.object({ from: localDate.optional(), to: localDate.optional() });
export const auditListQuery = paginationQuery.extend({
  entityType: z.string().max(48).optional(),
  entityId: z.uuid().optional(),
  actorId: z.uuid().optional(),
});
